import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu when location changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    setIsMenuOpen(false);
  };

  const navLinks = [
    { name: 'Forside', path: '/', type: 'link' },
    { name: 'Catering', path: '/#catering', type: 'anchor' },
    { name: 'Menu', path: '/#menu', type: 'anchor' },
    { name: 'Kontakt', path: '/kontakt', type: 'link' },
  ];

  return (
    <>
      <nav 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-zinc-900/95 backdrop-blur-md h-20' 
            : 'bg-transparent h-24'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
          <div className="flex justify-between items-center h-full">
            <div className="flex items-center">
              <Link to="/" onClick={scrollToTop} className="flex items-center">
                <span className="text-2xl font-bold tracking-tight text-white">
                  House of <span className="text-amber-500">Falafel</span>
                </span>
              </Link>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              {navLinks.map((link) => (
                link.type === 'link' ? (
                  <Link 
                    key={link.name}
                    to={link.path} 
                    onClick={link.path === '/' ? scrollToTop : undefined}
                    className={`transition-colors font-medium ${isScrolled ? 'text-zinc-300 hover:text-amber-500' : 'text-white hover:text-amber-500'}`}
                  >
                    {link.name}
                  </Link>
                ) : (
                  <a 
                    key={link.name}
                    href={link.path} 
                    className={`transition-colors font-medium ${isScrolled ? 'text-zinc-300 hover:text-amber-500' : 'text-white hover:text-amber-500'}`}
                  >
                    {link.name}
                  </a>
                )
              ))}
              <Link to="/kontakt" className="bg-amber-600 hover:bg-amber-700 text-white px-5 py-2.5 rounded-full transition-all font-semibold flex items-center justify-center shadow-lg shadow-amber-600/20">
                Book nu
              </Link>
            </div>

            <div className="md:hidden flex items-center">
              <button 
                onClick={() => setIsMenuOpen(true)}
                className="text-white p-2 focus:outline-none"
                aria-label="Open menu"
              >
                <Menu size={28} />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMenuOpen(false)}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] md:hidden"
            />
            
            {/* Sidebar */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed top-0 right-0 bottom-0 w-[80%] max-w-sm bg-zinc-900 z-[70] md:hidden flex flex-col shadow-2xl"
            >
              <div className="flex items-center justify-between p-6 border-b border-zinc-800">
                <Link to="/" onClick={scrollToTop} className="flex items-center">
                  <span className="text-xl font-bold tracking-tight text-white">
                    House of <span className="text-amber-500">Falafel</span>
                  </span>
                </Link>
                <button 
                  onClick={() => setIsMenuOpen(false)}
                  className="text-zinc-400 hover:text-white transition-colors"
                >
                  <X size={28} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto py-8 px-6">
                <div className="flex flex-col space-y-6">
                  {navLinks.map((link) => (
                    link.type === 'link' ? (
                      <Link 
                        key={link.name}
                        to={link.path} 
                        onClick={link.path === '/' ? scrollToTop : () => setIsMenuOpen(false)}
                        className="text-2xl font-semibold text-white hover:text-amber-500 transition-colors"
                      >
                        {link.name}
                      </Link>
                    ) : (
                      <a 
                        key={link.name}
                        href={link.path} 
                        onClick={() => setIsMenuOpen(false)}
                        className="text-2xl font-semibold text-white hover:text-amber-500 transition-colors"
                      >
                        {link.name}
                      </a>
                    )
                  ))}
                </div>
              </div>

              <div className="p-6 border-t border-zinc-800 bg-zinc-900/50">
                <Link 
                  to="/kontakt" 
                  onClick={() => setIsMenuOpen(false)}
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white py-4 rounded-xl transition-all font-bold flex items-center justify-center text-lg shadow-xl shadow-amber-600/20 group"
                >
                  <span>Book nu</span>
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                </Link>
                <p className="mt-6 text-center text-zinc-500 text-sm">
                  &copy; {new Date().getFullYear()} House of Falafel
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;

